import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, AlertCircle, Check, XIcon } from "lucide-react";
import { Link } from "react-router-dom";
export const EmailListenerDoc = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent className="min-w-[800px] p-0">
        <Card className="w-full max-h-[calc(100dvh_-_40px)] overflow-y-auto  bg-white border-gray-200 text-gray-900 shadow-md">
          <CardHeader className="border-b border-gray-100 pb-6 flex justify-between items-center flex-row">
            <CardTitle className="text-2xl font-bold">
              Set up an app password
            </CardTitle>
            <AlertDialogCancel className="p-3">
              <XIcon />
            </AlertDialogCancel>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-6">
              <div>
                <p className="text-gray-600 mt-1">
                  To allow us to access your emails, you’ll need to set up an
                  <span className="font-semibold"> app password</span>. This
                  enables us to read your emails securely without requiring your
                  actual email password.
                </p>
                <p className="text-gray-600 mt-1 mb-3">
                  Follow the instructions for your email provider:
                </p>
                <ul className="space-y-4 pl-1">
                  <li className="flex items-start gap-2">
                    <div className="h-6 w-6 flex items-center justify-center rounded-full bg-green-100 text-green-600 mt-0.5 flex-shrink-0">
                      <Check className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="font-semibold">Gmail (Google):</span>{" "}
                      <Link
                        to={
                          "https://support.google.com/mail/answer/185833?hl=en"
                        }
                        target="_blank"
                        className="text-green-600 hover:text-green-700 underline"
                      >
                        Set up an app password
                      </Link>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="h-6 w-6 flex items-center justify-center rounded-full bg-green-100 text-green-600 mt-0.5 flex-shrink-0">
                      <Check className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="font-semibold">
                        Outlook / Microsoft:
                      </span>{" "}
                      <Link
                        to="https://support.microsoft.com/en-us/account-billing/how-to-get-and-use-app-passwords-5896ed9b-4263-e681-128a-a6f2979a7944"
                        className="text-green-600 hover:text-green-700 underline"
                        target="_blank"
                      >
                        Create an app password
                      </Link>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="h-6 w-6 flex items-center justify-center rounded-full bg-green-100 text-green-600 mt-0.5 flex-shrink-0">
                      <Check className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="font-semibold">Yahoo Mail:</span>{" "}
                      <Link
                        to="https://help.yahoo.com/kb/SLN15241.html"
                        className="text-green-600 hover:text-green-700 underline"
                        target="_blank"
                      >
                        Generate an app password
                      </Link>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="h-6 w-6 flex items-center justify-center rounded-full bg-green-100 text-green-600 mt-0.5 flex-shrink-0">
                      <Check className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="font-semibold">
                        iCloud / Apple Mail:
                      </span>{" "}
                      <Link
                        to="https://support.apple.com/en-us/HT204397"
                        className="text-green-600 hover:text-green-700 underline"
                        target="_blank"
                      >
                        Generate an app-specific password
                      </Link>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="h-6 w-6 flex items-center justify-center rounded-full bg-green-100 text-green-600 mt-0.5 flex-shrink-0">
                      <Check className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-semibold">
                        Self-hosted or custom email (IMAP/SMTP):
                      </div>
                      <p className="text-gray-600 mt-1">
                        You’ll need to generate or configure access credentials
                        through your email admin panel or hosting provider. Make
                        sure:
                      </p>
                      <ul className="mt-2 space-y-2 pl-6 list-disc text-gray-600">
                        <li>IMAP access is enabled</li>
                        <li>
                          You use a separate user account or password for
                          programmatic access
                        </li>
                        <li>
                          Two-factor authentication is disabled or an
                          app-specific password is generated (if supported)
                        </li>
                      </ul>
                    </div>
                  </li>
                </ul>
              </div>

              <Alert className="bg-green-50 border-green-100 text-gray-800">
                <Mail className="h-5 w-5 text-green-600 -translate-y-1" />
                <AlertDescription className="text-gray-800 font-medium text-base">
                  Important Notes:
                </AlertDescription>
                <div className="mt-2 space-y-2 pl-6 text-gray-600">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <p>
                      We only read{" "}
                      <span className="font-bold text-gray-800">
                        unread emails
                      </span>
                      .
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <p>
                      If{" "}
                      <span className="font-bold text-gray-800">
                        multiple people
                      </span>{" "}
                      use the same email account, we strongly recommend creating
                      a{" "}
                      <span className="font-bold text-gray-800">
                        dedicated email address solely for receiving orders
                      </span>{" "}
                      to avoid confusion or missed messages.
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <p>
                      <span className="font-bold text-gray-800">
                        App passwords cannot be used by multiple services at the
                        same time
                      </span>
                      . If another email listener or application is using the
                      same app password simultaneously, it may cause access
                      issues or connection failures.
                    </p>
                  </div>
                </div>
              </Alert>
            </div>
          </CardContent>
          <AlertDialogFooter className="bg-white sticky bottom-0 py-5 px-5">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </Card>
      </AlertDialogContent>
    </AlertDialog>
  );
};
